import { api } from '@/lib/api';
import type { QuoteStatusStats, V3Quote, CreateQuoteInput, UpdateQuoteInput } from './types';

// =============================================================================
// Re-export read APIs from quotes
// =============================================================================

export {
  getQuotes,
  getQuote,
  getQuoteByNumber,
  getQuoteHtml,
  getCustomerOrganisations,
  getSalesUsers,
  getOrganisationUsers,
  getContractsByCustomer,
  getSitesByCustomer,
} from '@/features/quotes/api';

// =============================================================================
// Order-specific API Functions
// =============================================================================

/**
 * Get order stats from the new V4 server-side endpoint
 */
export async function getOrderStats(filters: {
  accountId?: string;
  salesManagerId?: string;
  status_in?: string[];
} = {}): Promise<QuoteStatusStats[]> {
  const queryParams: Record<string, string> = {};

  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }
  if (filters.salesManagerId) {
    queryParams.salesManagerId = filters.salesManagerId;
  }
  if (filters.status_in && filters.status_in.length > 0) {
    queryParams.status_in = JSON.stringify(filters.status_in);
  }

  const result = await api.get<QuoteStatusStats[]>('/v4/quotes/stats', queryParams);
  // Normalize: server returns count/amount, frontend expects quantity/amount
  return result.map(stat => ({
    status: stat.status,
    quantity: (stat as any).count ?? stat.quantity,
    amount: stat.amount,
  }));
}

/**
 * Get a single order for editing (V3 - full MongoDB data)
 */
export async function getOrderForEdit(id: string): Promise<V3Quote> {
  const response = await api.get<{ quote: V3Quote }>(`/v4/legacy/quotes/${id}`, {
    populate: JSON.stringify([
      'customer',
      'contract',
      'sales_manager',
      'contractual_manager',
      'billing_site',
      'shipping_addresses.site',
      'po_file',
    ]),
  });
  return response.quote;
}

/**
 * Create a new order (V3 - uses MongoDB)
 */
export async function createOrder(data: CreateQuoteInput): Promise<V3Quote> {
  const response = await api.post<{ quote: V3Quote }>('/v4/legacy/quotes', data);
  return response.quote;
}

/**
 * Update an order (V3 - uses MongoDB)
 */
export async function updateOrder(id: string, data: UpdateQuoteInput): Promise<V3Quote> {
  const response = await api.put<{ quote: V3Quote }>(`/v4/legacy/quotes/${id}`, data);
  return response.quote;
}
